import React, { useEffect, useRef } from "react";
import { IconButton, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import TitleIcon from "@mui/icons-material/Title";
import LinkIcon from "@mui/icons-material/Link";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

/**
 * Lightweight rich text surface — not TinyMCE / CKEditor.
 * Styled for Knowledge Base navy theme.
 */
const KBRichEditor = ({
  value = "",
  onChange,
  placeholder = "Start writing your content…",
}) => {
  const ref = useRef(null);
  const lastHtml = useRef(value);

  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastHtml.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
      lastHtml.current = value || "";
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastHtml.current = html;
    onChange?.(html);
  };

  const run = (command, arg = null) => {
    ref.current?.focus();
    try {
      document.execCommand(command, false, arg);
    } catch (_) {
      /* ignore unsupported commands */
    }
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Enter link URL");
    if (!url) return;
    run("createLink", url.trim());
  };

  const tools = [
    { title: "Bold", icon: <FormatBoldIcon fontSize="small" />, action: () => run("bold") },
    { title: "Italic", icon: <FormatItalicIcon fontSize="small" />, action: () => run("italic") },
    { title: "Underline", icon: <FormatUnderlinedIcon fontSize="small" />, action: () => run("underline") },
    { divider: true },
    { title: "Heading", icon: <TitleIcon fontSize="small" />, action: () => run("formatBlock", "h3") },
    { title: "Quote", icon: <FormatQuoteIcon fontSize="small" />, action: () => run("formatBlock", "blockquote") },
    { divider: true },
    { title: "Bullets", icon: <FormatListBulletedIcon fontSize="small" />, action: () => run("insertUnorderedList") },
    { title: "Numbers", icon: <FormatListNumberedIcon fontSize="small" />, action: () => run("insertOrderedList") },
    { title: "Link", icon: <LinkIcon fontSize="small" />, action: insertLink },
    { divider: true },
    { title: "Undo", icon: <UndoIcon fontSize="small" />, action: () => run("undo") },
    { title: "Redo", icon: <RedoIcon fontSize="small" />, action: () => run("redo") },
    { title: "Clear format", icon: <FormatClearIcon fontSize="small" />, action: () => run("removeFormat") },
  ];

  return (
    <div className="kb-rich">
      <div className="kb-rich-toolbar" role="toolbar" aria-label="Formatting">
        {tools.map((tool, idx) =>
          tool.divider ? (
            <span key={`d-${idx}`} className="kb-rich-divider" aria-hidden />
          ) : (
            <Tooltip key={tool.title} title={tool.title} arrow>
              <IconButton
                type="button"
                size="small"
                className="kb-rich-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={tool.action}
              >
                {tool.icon}
              </IconButton>
            </Tooltip>
          ),
        )}
      </div>
      <div
        ref={ref}
        className="kb-rich-surface"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        role="textbox"
        aria-multiline="true"
      />
    </div>
  );
};

export default KBRichEditor;
